import React, { CSSProperties } from 'react';
import '../../field.scss';
import '../../validation.scss';
import InputLabel from '../inputLabel/InputLabel';
import { CommonValidation } from '../commonValidation/commonValidation';

export type TextInputProps = {
    name: string;
    className?: string;
    error?: string;
    placeholder?: string;
    label?: string;
    value?: string;
    dataKey?: string;
    maxLength?: number;
    readOnly?: boolean;
    autoFocus?: boolean;
    focus?: boolean;
    handleReturn?: boolean;
    stopPropagationOnClick?: boolean;
    floatingLabel?: boolean;
    maxLengthWidth?: boolean;
    numeric?: boolean;
    ariaDescribeLink?: string;
    children?: React.ReactNode;
    onFocus?: (event?: React.FormEvent<HTMLInputElement>) => void;
    onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onChange?: (event: React.FormEvent<HTMLInputElement>, key?: string) => void;
    onLeave?: (event: React.FormEvent<HTMLInputElement>, key?: string) => void;
};

/** A generic text input that is used as an all purpose data entry box */
const TextInput = (props: TextInputProps) => {
    const {
        name,
        placeholder,
        value,
        dataKey = '',
        maxLength = 10000,
        className,
        stopPropagationOnClick,
        ariaDescribeLink = '',
        handleReturn,
        autoFocus,
        label,
        error,
        readOnly,
        focus,
        floatingLabel,
        numeric,
        maxLengthWidth,
        onChange,
        onLeave,
        onKeyPress,
        onFocus,
        children
    } = props;

    // add a handler to trim the values on leave ...
    const onLeaveInput = (event: React.FormEvent<HTMLInputElement>) => {
        let value = event.currentTarget.value;

        if (value !== undefined) {
            value = value.trim();
            event.currentTarget.value = value;

            onChange && onChange(event, dataKey);
        }

        if (onLeave !== undefined) {
            onLeave(event, dataKey);
        }
    };

    const onClickInput = (event: React.FormEvent<HTMLInputElement>) => {
        if (stopPropagationOnClick) {
            event.stopPropagation();
        }
    };

    const onKeyPressed = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (handleReturn) {
            if (event.key == 'Enter') {
                // eslint-disable-next-line  @typescript-eslint/no-explicit-any
                const blurEvent: any = { currentTarget: { value: null } };
                onLeaveInput(blurEvent); //eslint-disable-line
            }
        }

        if (onKeyPress !== undefined) {
            onKeyPress(event);
        }
    };

    let styleWrapper = 'form-group';
    if (!CommonValidation.isEmpty(error)) {
        styleWrapper += ' has-error';
    }

    let baseClasses = 'field ';
    if (className) {
        baseClasses += className;
    }

    let inputInnerStyle = 'form-control';
    if (floatingLabel && label) {
        inputInnerStyle += ' field ';

        inputInnerStyle += error ? 'is-invalid' : '';
    }

    let inputStyle: CSSProperties = {};
    if (maxLengthWidth) {
        inputStyle = {
            ...inputStyle,
            width: `calc(${maxLength} * 1ch + 0.5ch + 1.5em)`,
            paddingLeft: '0.75em',
            paddingRight: '0.75em'
        };
    }

    const createLabel = (): JSX.Element => {
        if (label !== undefined) {
            return <InputLabel labelText={label} name={name} />;
        }
        return <></>;
    };

    const onChangeInput = (
        event: React.FormEvent<HTMLInputElement>,
        key: string
    ) => {
        if (
            numeric &&
            !CommonValidation.isValidNumeric(event.currentTarget.value)
        ) {
            return;
        }
        onChange && onChange(event, key);
    };

    const createRenderPanel = (): JSX.Element => {
        return (
            <input
                style={inputStyle}
                id={name}
                name={name}
                aria-label={name}
                aria-describedby={ariaDescribeLink}
                type='text'
                autoComplete='off'
                className={inputInnerStyle}
                placeholder={placeholder || label}
                autoFocus={autoFocus}
                value={value}
                onKeyPress={onKeyPressed}
                onChange={(e) => onChangeInput(e, dataKey)}
                onFocus={onFocus}
                onBlur={onLeaveInput}
                onClick={onClickInput}
                maxLength={maxLength}
                readOnly={readOnly}
                ref={(input) => input && focus && input.focus()}
            />
        );
    };

    const labelControl = createLabel();
    const inputControl = createRenderPanel();

    const createFinalControlSolo = () => {
        if (floatingLabel && label) {
            let wrapper = 'form-floating ';
            let errorColourStyle = '';

            if (error) {
                wrapper += 'has-error';
                errorColourStyle = 'floating-label-validation-color';
            }

            return (
                <div>
                    <div className={wrapper}>
                        {inputControl}
                        <label htmlFor={name}>{label}</label>
                    </div>
                    <div className={errorColourStyle}>{error}</div>
                </div>
            );
        } else {
            return (
                <div className={styleWrapper}>
                    {labelControl}
                    <div className={baseClasses}>{inputControl}</div>
                    <div className='input'>{error}</div>
                </div>
            );
        }
    };

    const createFinalControlJoined = () => {
        return (
            <div className={styleWrapper}>
                {labelControl}
                <div className='field input-group'>
                    {inputControl}
                    {children}
                </div>
                <div className='input'>{error}</div>
            </div>
        );
    };
    const finalControl =
        children == undefined
            ? createFinalControlSolo()
            : createFinalControlJoined();

    return <>{finalControl}</>;
};

export default TextInput;
